import React, { useState } from "react";
import Icon from "../components/Icon";
import Reveal from "../components/Reveal";
import HeroBackdrop from "../components/HeroBackdrop";
import Button from "../components/Button";
import Card3D from "../components/Card3D";
import Counter from "../components/Counter";
import HeroStage from "../components/HeroStage";
import SectionHeading from "../components/SectionHeading";
import DownloadDialog from "../components/DownloadDialog";
import useParallax from "../hooks/useParallax";
import { HOME_WORLDS } from "../data/heroWorlds";


const SERVICES = [
  { key: "restaurants", title: "Restaurants", desc: "Order from the best kitchens near you, or reserve a table in seconds." },
  { key: "fashion", title: "Fashion", desc: "Clothing, footwear and accessories from local and global brands." },
  { key: "gifts", title: "Gifts", desc: "Curated gifting for every occasion, delivered where it needs to go." },
  { key: "electronics", title: "Electronics", desc: "Phones, laptops and home appliances from verified sellers." },
  { key: "home", title: "Home", desc: "Furniture and everything else that turns a place into a home." },
  { key: "cars", title: "Cars", desc: "Browse, book and rent vehicles from top brands and trusted dealers." },
  { key: "realEstate", title: "Real Estate", desc: "Buy, rent or list property with listings you can actually trust." },
  { key: "healthcare", title: "Healthcare", desc: "Connect with doctors, clinics and pharmacies without the wait." },
  { key: "tourism", title: "Tourism", desc: "Over 350,000 hotels worldwide, plus flights, chalets and packages." }
];

/* The travel and connectivity inventory sitting behind the booking
   categories. `value` is what counts up; anything that isn't a digit rides
   along as `suffix` so the count-up still plays. */
const GLOBAL_KPIS = [
  { icon: "send", value: 460, label: "Airlines Partners" },
  { icon: "tourism", value: 2, suffix: "M", label: "Hotels Worldwide" },
  { icon: "map", value: 250, label: "Carriers" },
  { icon: "smartphone", value: 200, label: "eSIM Service" }
];

const STORES = [
  { icon: "play", label: "Google Play" },
  { icon: "apple", label: "App Store" },
  { icon: "store", label: "AppGallery" }
];

const BOOKINGS = [
  "Restaurant bookings",
  "Hotel bookings",
  "Holiday packages",
  "Car bookings",
  "Chalet bookings",
  "Cinema bookings",
  "Bus bookings",
  "B2B bookings"
];

const PILLARS = [
  {
    icon: "sparkle",
    title: "One account, everything",
    desc: "Shopping, travel, food and services under a single login — no app switching, no repeated checkouts."
  },
  {
    icon: "shield",
    title: "A marketplace you can trust",
    desc: "Businesses are verified before they list, so every seller, product and booking is accountable."
  },
  {
    icon: "bolt",
    title: "Built for Jordan, ready for more",
    desc: "Designed around how people here actually shop and travel, on infrastructure that scales past the region."
  }
];

const STEPS = [
  { n: "01", title: "Download Mondoway", desc: "Free on Google Play, the App Store and Huawei AppGallery." },
  { n: "02", title: "Pick a category", desc: "Nine categories on one home screen — from dinner tonight to a flight next month." },
  { n: "03", title: "Book, buy or sell", desc: "Check out once. Track orders, bookings and listings from a single place." }
];


function HomePage({ onNavigate }) {
  const [downloadOpen, setDownloadOpen] = useState(false);

  // The app shot drifts with the scroll, so it reads as sitting nearer the
  // reader than the panel it sits in. The hero runs its own pointer-driven
  // parallax and takes no part in this.
  const appShot = useParallax({ speed: 34 });

  const handleNavigation = (tabName) => {
    if (tabName === "Services" && onNavigate) {
      onNavigate("services");
    } else if (tabName === "About Us" && onNavigate) {
      onNavigate("about");
    } else if (tabName === "Contact Us" && onNavigate) {
      onNavigate("contact");
    } else if (tabName === "Delete Business User" && onNavigate) {
      onNavigate("deleteBusinessUser");
    }
  };

  return (
    <div className="bg-[#FAF8F2] text-[#1E1B16]">
      
      {/* ============================== HERO ==============================
          A scene rather than a band: the phone at its centre turns towards the
          pointer, the glass orbs around it are pushed away by it, and picking
          a world on the right re-lights the gradient and re-scatters the
          field. The stats rail rides inside the same gradient, so the band
          reads as one place instead of two dark sections stacked. */}
      <HeroStage
        worlds={HOME_WORLDS}
        eyebrow={
          <span className="inline-flex w-fit items-center gap-2.5 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[13px] font-semibold tracking-wide text-white backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-[#FBCFE8] ring-4 ring-[#FBCFE8]/20" />
            Jordan&rsquo;s first all-in-one platform
          </span>
        }
        title={
          <>
            Everything
            <br />
            <span className="bg-[linear-gradient(100deg,#FBCFE8_0%,#E9D5FF_45%,#FFFFFF_100%)] bg-clip-text text-transparent">
              in one app.
            </span>
          </>
        }
        lede={
          <>
            Shopping, travel, food delivery and a marketplace of verified
            businesses — one account, one checkout. Nine services, no app
            switching.
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => setDownloadOpen(true)}
              className="group flex items-center gap-5 rounded-full bg-black/45 py-1.5 pl-6 pr-1.5 text-[15px] font-bold text-white ring-1 ring-white/15 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-black/65 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FBCFE8]/50"
            >
              Download the app
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FBCFE8] text-[#3B0764] transition-transform duration-500 [transition-timing-function:var(--mw-spring)] group-hover:rotate-90">
                <Icon name="arrow" className="h-[18px] w-[18px]" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleNavigation("Services")}
              className="rounded-full border border-white/20 px-6 py-3 text-[15px] font-semibold text-white/90 transition-colors duration-300 hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white/25"
            >
              Explore services
            </button>
          </>
        }
        footnote={
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/[0.07] text-[#FBCFE8] backdrop-blur">
              <Icon name="checkCircle" className="h-[22px] w-[22px]" />
            </span>
            <span className="flex flex-col">
              <span className="mw-eyebrow text-[#B9A0D6]">Available on</span>
              <span className="text-[0.85rem] font-semibold text-white">
                Google Play · App Store · AppGallery
              </span>
            </span>
          </div>
        }
        sideTitle={
          <>
            Endlessly
            <br />
            <span className="bg-[linear-gradient(100deg,#FFFFFF_0%,#E9D5FF_55%,#FBCFE8_100%)] bg-clip-text text-transparent">
              simple.
            </span>
          </>
        }
      >
        <div className="mx-auto max-w-7xl px-6 pb-20 md:px-10">
          {/* -------------------------- Stats rail --------------------------
              The reach behind the app, closing the hero. Each card swings up
              out of the page on reveal, then tilts under the pointer with its
              icon, number and label held at three different depths — so the
              turn produces parallax between them instead of tipping a picture.

              The glass is a child plate rather than a tone on the face: the
              blur is a grouping property and would flatten everything above
              it. Same reason the sweep clips inside its own wrapper. */}
          <Reveal>
            <div className="border-t border-white/10 pt-8">
              <span className="mw-eyebrow text-[#B9A0D6]">Global presence</span>
            </div>
          </Reveal>

          <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {GLOBAL_KPIS.map((kpi, i) => (
              <Reveal
                as="li"
                key={kpi.label}
                delay={i * 120}
                className="mw-reveal-3d"
              >
                <Card3D
                  tone="plate"
                  max={12}
                  lift={10}
                  className="h-full"
                  innerClassName="p-6"
                >
                  <span
                    className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/[0.14] bg-white/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_30px_60px_-30px_rgba(0,0,0,0.75)] backdrop-blur-[20px] transition-all duration-500 group-hover:border-[#D8B4FE]/40 group-hover:bg-white/[0.1] group-hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_40px_70px_-28px_rgba(168,85,247,0.55)]"
                    aria-hidden="true"
                  />
                  <span
                    className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
                    aria-hidden="true"
                  >
                    <span className="mw-sweep absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(100deg,transparent,rgba(255,255,255,0.18),transparent)]" />
                  </span>

                  <span className="mw-z3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[linear-gradient(140deg,rgba(216,180,254,0.3),rgba(106,13,173,0.5))] text-[#E9D5FF] shadow-[0_14px_30px_-14px_rgba(168,85,247,0.9)] ring-1 ring-white/20 transition-all duration-500 [transition-timing-function:var(--mw-spring)] group-hover:bg-[linear-gradient(140deg,#8B2BD4,#4C087D)] group-hover:text-white group-hover:shadow-[0_22px_42px_-14px_rgba(168,85,247,1)] group-hover:ring-[#D8B4FE]/60">
                    <Icon name={kpi.icon} className="h-[22px] w-[22px]" />
                  </span>
                  <span className="mw-z2 mt-6 block text-[2rem] font-bold leading-none tracking-tight text-white sm:text-[2.35rem]">
                    <Counter value={kpi.value} prefix="+" suffix={kpi.suffix} />
                  </span>
                  <span className="mw-z1 mt-2.5 block text-[13px] font-medium leading-snug text-[#B9A0D6] transition-colors duration-500 group-hover:text-[#DCC9F2]">
                    {kpi.label}
                  </span>
                </Card3D>
              </Reveal>
            ))}
          </ul>
        </div>
      </HeroStage>

      {/* ============================ MARQUEE ============================= */}
      <section className="border-b border-[#EDE4D8] py-8" aria-label="Service categories">
        <div className="mw-marquee mw-fade-x overflow-hidden">
          {/* pr-3 matches the gap so the -50% loop lands on an exact seam */}
          <div className="mw-marquee-track flex gap-3 pr-3">
            {[...SERVICES, ...SERVICES].map((service, i) => (
              <span
                key={`${service.key}-${i}`}
                className="inline-flex shrink-0 items-center gap-2.5 rounded-full border border-[#EAE2D6] bg-white px-5 py-2.5 text-[15px] font-medium text-[#4D4B46]"
              >
                <Icon name={service.key} className="h-[18px] w-[18px] text-[#6A0DAD]" />
                {service.title}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= ABOUT MONDOWAY ========================= */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
            <div className="order-2 lg:order-1 lg:col-span-5">
              <Reveal>
                <div className="relative overflow-hidden rounded-3xl border border-[#EAE2D6] bg-white shadow-[0_30px_60px_-30px_rgba(30,27,22,0.25)]">
                  <img
                    src="/Images/1.png"
                    alt="Mondoway brand illustration: travel, cars, property and shopping connected in one platform"
                    className="w-full"
                    loading="lazy"
                  />
                </div>
              </Reveal>

              <Reveal delay={130}>
                <figure className="relative mt-6 overflow-hidden rounded-3xl bg-[linear-gradient(150deg,#4C087D_0%,#6A0DAD_55%,#3B0764_100%)] p-9 text-white shadow-[0_30px_60px_-28px_rgba(106,13,173,0.55)]">
                  <div
                    className="mw-grain absolute inset-0 opacity-[0.14] mix-blend-overlay"
                    aria-hidden="true"
                  />
                  <Icon
                    name="quote"
                    filled
                    className="relative h-9 w-9 text-white/30"
                  />
                  <blockquote className="relative mt-4 text-lg font-medium leading-relaxed">
                    Simplify daily life by bringing everything people need into one
                    seamless, trustworthy platform.
                  </blockquote>
                  <figcaption className="relative mt-6 border-t border-white/20 pt-5 text-sm font-semibold tracking-wide text-[#E9D5FF]">
                    Our mission
                  </figcaption>
                </figure>
              </Reveal>
            </div>

            <div className="order-1 lg:order-2 lg:col-span-7">
              <Reveal>
                <SectionHeading
                  eyebrow="About Mondoway"
                  title={
                    <>
                      Nine services. One platform.
                      <br className="hidden sm:block" /> Zero friction.
                    </>
                  }
                />
              </Reveal>

              <Reveal delay={110}>
                <div className="mt-8 space-y-5">
                  <p className="mw-lede text-[#4D4B46]">
                    Mondoway is Jordan&rsquo;s first and only all-in-one smart
                    application — built for people who are tired of juggling a
                    different app for every part of their day.
                  </p>
                  <p className="mw-lede text-[#4D4B46]">
                    Order dinner, book a hotel, rent a car, find an apartment, reach a
                    doctor or shop the latest electronics. It all happens in one place,
                    with one account and one checkout.
                  </p>
                </div>
              </Reveal>

              <div className="mt-10 space-y-4">
                {PILLARS.map((pillar, i) => (
                  <Reveal key={pillar.title} delay={170 + i * 90}>
                    <Card3D max={5} lift={4} innerClassName="flex gap-5 p-6">
                      <span className="mw-z2 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#F4E8FF] to-[#EADCFB] text-[#6A0DAD] shadow-[0_10px_25px_-14px_rgba(106,13,173,0.6)] ring-1 ring-[#EBDBFA] transition-all duration-500 [transition-timing-function:var(--mw-spring)] group-hover:from-[#6A0DAD] group-hover:to-[#4C087D] group-hover:text-white group-hover:ring-[#6A0DAD]">
                        <Icon name={pillar.icon} className="h-6 w-6" />
                      </span>
                      <span className="mw-z1">
                        <span className="block text-lg font-semibold text-[#1E1B16]">
                          {pillar.title}
                        </span>
                        <span className="mt-1.5 block text-[15px] leading-relaxed text-[#4D4B46]">
                          {pillar.desc}
                        </span>
                      </span>
                    </Card3D>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={450}>
                <button
                  onClick={() => handleNavigation("About Us")}
                  className="group mt-10 inline-flex items-center gap-2 text-base font-semibold text-[#6A0DAD] transition-colors duration-300 hover:text-[#4C087D]"
                >
                  Read the full story
                  <Icon
                    name="arrow"
                    className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= SERVICES =========================== */}
      <section className="relative border-y border-[#EDE4D8] bg-gradient-to-b from-white via-white to-[#FBF7FF] py-24 lg:py-32">
        <div className="mw-grid-light absolute inset-0" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="Our services"
              title="Everything you need, organised."
              lede="Nine categories, each with its own verified partners — and all of them one tap away from the same home screen."
            />
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service, i) => (
              <Reveal key={service.title} delay={(i % 3) * 80} className="h-full">
                <Card3D
                  as="button"
                  onClick={() => handleNavigation("Services")}
                  className="h-full"
                  innerClassName="p-7"
                >
                  {/* The icon stands furthest off the face, the type just
                      behind it — that spread is what the tilt turns into
                      parallax rather than a card tipping as one flat plane. */}
                  <span className="mw-z3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F4E8FF] to-[#EADCFB] text-[#6A0DAD] shadow-[0_10px_25px_-12px_rgba(106,13,173,0.6)] ring-1 ring-[#EBDBFA] transition-all duration-500 [transition-timing-function:var(--mw-spring)] group-hover:from-[#6A0DAD] group-hover:to-[#4C087D] group-hover:text-white group-hover:shadow-[0_18px_35px_-14px_rgba(106,13,173,0.8)] group-hover:ring-[#6A0DAD]">
                    <Icon name={service.key} className="h-6 w-6" />
                  </span>
                  <span className="mw-z1 mt-6 flex items-center gap-2 text-xl font-semibold tracking-tight text-[#1E1B16]">
                    {service.title}
                    <Icon
                      name="arrow"
                      className="h-4 w-4 -translate-x-1 text-[#6A0DAD] opacity-0 transition-all duration-500 [transition-timing-function:var(--mw-spring)] group-hover:translate-x-0 group-hover:opacity-100"
                    />
                  </span>
                  <span className="mw-z1 mt-2.5 block text-[15px] leading-relaxed text-[#4D4B46]">
                    {service.desc}
                  </span>
                </Card3D>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="mt-14 text-center">
              <Button
                magnetic
                icon="arrow"
                onClick={() => handleNavigation("Services")}
              >
                Explore all services
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =========================== INSIDE THE APP ======================= */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="overflow-hidden rounded-[2rem] border border-[#EDE0FA] bg-[linear-gradient(140deg,#F8F1FF_0%,#FAF8F2_60%,#F3E9FE_100%)]">
            <div className="grid items-center gap-12 p-8 sm:p-12 lg:grid-cols-2 lg:gap-10 lg:p-16">
              <div>
                <Reveal>
                  <SectionHeading
                    eyebrow="Inside the app"
                    title="Every booking lives here."
                    lede="Restaurants, hotels, cars, cinema, chalets and full holiday packages — booked and tracked from one screen."
                  />
                </Reveal>

                <Reveal delay={120}>
                  <ul className="mt-9 flex flex-wrap gap-2.5">
                    {BOOKINGS.map((item) => (
                      <li
                        key={item}
                        className="rounded-full border border-[#EADCFB] bg-white px-4 py-2 text-sm font-medium text-[#4C087D] shadow-[0_1px_2px_rgba(30,27,22,0.04)]"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>

                <Reveal delay={220}>
                  <Button
                    magnetic
                    icon="arrow"
                    className="mt-10"
                    onClick={() => handleNavigation("Services")}
                  >
                    See what&rsquo;s included
                  </Button>
                </Reveal>
              </div>

              <Reveal delay={160}>
                <div
                  ref={appShot}
                  className="mw-parallax overflow-hidden rounded-2xl border border-[#E6D6F8] shadow-[0_40px_70px_-35px_rgba(59,7,100,0.6)]"
                >
                  <img
                    src="/Images/Screenshot 2025-09-15 012114.png"
                    alt="Mondoway app screens showing the home feed, bookings menu and rewards"
                    className="w-full"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* =========================== HOW IT WORKS ========================= */}
      <section className="border-t border-[#EDE4D8] bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="How it works"
              title="Three steps. That's it."
              align="center"
            />
          </Reveal>

          <div className="relative mt-16">
            <div
              className="absolute left-0 right-0 top-[3.4rem] hidden h-px bg-gradient-to-r from-transparent via-[#E4D3F7] to-transparent md:block"
              aria-hidden="true"
            />
            <div className="relative grid gap-5 md:grid-cols-3">
              {STEPS.map((step, i) => (
                <Reveal key={step.n} delay={i * 110} className="h-full">
                  <Card3D tone="glass" max={6} lift={6} className="h-full" innerClassName="p-8 text-center">
                    <span className="mw-z3 mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(140deg,#7B1BC4,#4C087D)] text-base font-bold text-white shadow-[0_16px_30px_-12px_rgba(106,13,173,0.75)]">
                      {step.n}
                    </span>
                    <h3 className="mw-z1 mt-6 text-xl font-semibold tracking-tight text-[#1E1B16]">
                      {step.title}
                    </h3>
                    <p className="mw-z1 mt-2.5 text-[15px] leading-relaxed text-[#4D4B46]">
                      {step.desc}
                    </p>
                  </Card3D>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =============================== CTA ============================== */}
      <section className="relative isolate overflow-hidden bg-[#220339]">
        <HeroBackdrop tone="cta" />

        <div className="relative mx-auto max-w-4xl px-6 py-28 text-center md:px-10 lg:py-32">
          <Reveal>
            <h2 className="mw-h2 text-white">Ready to simplify your day?</h2>
            <p className="mw-lede mx-auto mt-6 max-w-2xl text-[#DCC9F2]">
              Join the people already running their shopping, travel and everyday
              bookings through a single app.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-11 flex flex-col justify-center gap-3.5 sm:flex-row">
              <Button
                magnetic
                variant="light"
                icon="arrow"
                onClick={() => handleNavigation("Contact Us")}
              >
                Get in touch
              </Button>
              <Button variant="glass" onClick={() => handleNavigation("About Us")}>
                Learn more
              </Button>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#B9A0D6]">
              {STORES.map((store) => (
                <span key={store.label} className="inline-flex items-center gap-2">
                  <Icon
                    name={store.icon}
                    filled={store.icon !== "store"}
                    className="h-4 w-4"
                  />
                  {store.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <DownloadDialog open={downloadOpen} onClose={() => setDownloadOpen(false)} />
    </div>
  );
}

export default HomePage;
